const IST_DATE_FORMATTER = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  weekday: "short",
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const IST_TIME_FORMATTER = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

const AMOUNT_FORMATTER = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
});

const NUMERIC_HISTORY_FIELDS = new Set([
  "rfqBudget",
  "quantity",
  "budget",
]);

const RFQ_TYPE_LABELS = {
  material: "Material",
  contractor: "Contractor with Material",
  labour: "Labour / Item Rate",
};

const STATUS_LABELS = {
  OPEN: "Open",
  CLOSED: "Closed",
  "PENDING APPROVAL": "Pending Approval",
  REWORK: "Rework",
  HOLD: "Hold",
  CANCELED: "Canceled",
  CANCELLED: "Cancelled",
  DELETED: "Deleted",
};

const TONE_CLASS_MAP = {
  orange: {
    dot: "bg-[#F97316]",
    ring: "ring-[#FED7AA]",
    card: "border-[#FED7AA] bg-[#FFF7ED]",
    badge: "bg-[#FFF1E6] text-[#C2410C]",
  },
  blue: {
    dot: "bg-[#2563EB]",
    ring: "ring-[#BFDBFE]",
    card: "border-[#DBEAFE] bg-[#EFF6FF]",
    badge: "bg-[#E8F1FF] text-[#1D4ED8]",
  },
  green: {
    dot: "bg-[#16A34A]",
    ring: "ring-[#BBF7D0]",
    card: "border-[#DCFCE7] bg-[#F0FDF4]",
    badge: "bg-[#EAFBF0] text-[#15803D]",
  },
  yellow: {
    dot: "bg-[#CA8A04]",
    ring: "ring-[#FEF08A]",
    card: "border-[#FDE68A] bg-[#FEFCE8]",
    badge: "bg-[#FEF3C7] text-[#A16207]",
  },
  purple: {
    dot: "bg-[#7C3AED]",
    ring: "ring-[#DDD6FE]",
    card: "border-[#EDE9FE] bg-[#F5F3FF]",
    badge: "bg-[#EFE9FF] text-[#6D28D9]",
  },
  red: {
    dot: "bg-[#DC2626]",
    ring: "ring-[#FECACA]",
    card: "border-[#FEE2E2] bg-[#FEF2F2]",
    badge: "bg-[#FEECEC] text-[#B91C1C]",
  },
  slate: {
    dot: "bg-[#475569]",
    ring: "ring-[#CBD5E1]",
    card: "border-[#E2E8F0] bg-[#F8FAFC]",
    badge: "bg-[#EEF2F7] text-[#334155]",
  },
};

const isValidDate = (value) => {
  if (!value) return false;
  const parsedDate = new Date(value);
  return !Number.isNaN(parsedDate.getTime());
};

const resolveTimelineTimestamp = (
  candidateValue,
  fallbackValue,
  minimumValue = null,
) => {
  const minimumTime = isValidDate(minimumValue)
    ? new Date(minimumValue).getTime()
    : null;
  const candidateTime = isValidDate(candidateValue)
    ? new Date(candidateValue).getTime()
    : null;
  const fallbackTime = isValidDate(fallbackValue)
    ? new Date(fallbackValue).getTime()
    : null;

  if (candidateTime !== null && (minimumTime === null || candidateTime >= minimumTime)) {
    return new Date(candidateTime).toISOString();
  }

  if (fallbackTime !== null && (minimumTime === null || fallbackTime >= minimumTime)) {
    return new Date(fallbackTime).toISOString();
  }

  if (minimumTime !== null) {
    return new Date(minimumTime).toISOString();
  }

  return null;
};

const sanitizeHtmlText = (value = "") =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const formatDateTimeParts = (value) => {
  if (!isValidDate(value)) return { date: "--", time: "" };

  const date = new Date(value);
  return {
    date: IST_DATE_FORMATTER.format(date).replace(/,\s(?=\d{4}$)/, " "),
    time: IST_TIME_FORMATTER.format(date).replace(/\s+/g, ""),
  };
};

const formatDateTime = (value) => {
  const { date, time } = formatDateTimeParts(value);
  return time ? `${date}, ${time}` : date;
};

const formatAmount = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "Rs. 0";
  return `Rs. ${AMOUNT_FORMATTER.format(amount)}`;
};

const formatTimelineChangeValue = (value, field) => {
  if (value === undefined || value === null || value === "") return "--";
  if (!NUMERIC_HISTORY_FIELDS.has(field)) return value;

  const number = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(number) ? AMOUNT_FORMATTER.format(number) : value;
};

const getFirstFiniteNumber = (item = {}, fields = []) => {
  for (const field of fields) {
    const value = item?.[field];
    if (value === undefined || value === null || value === "") continue;

    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }

  return null;
};

const getMaterialRate = (item = {}) => {
  const selectedRate = getFirstFiniteNumber(item, ["selectedItemRate"]);
  if (selectedRate !== null) return selectedRate;

  const baseRate = getFirstFiniteNumber(item, [
    "basePrice",
    "currentRate",
    "rate",
    "itemRate",
  ]);
  if (baseRate === null) return null;

  const discount = getFirstFiniteNumber(item, ["selectedDiscount", "discount"]);
  return discount === null ? baseRate : baseRate - (baseRate * discount) / 100;
};

const getLabourRate = (item = {}) =>
  getFirstFiniteNumber(item, [
    "selectedLabourRate",
    "labourRate",
    "currentRate",
    "rate",
    "itemRate",
  ]);

const getContractorRate = (scope = {}) => {
  const selectedRate = getFirstFiniteNumber(scope, ["selectedScopeRate"]);
  if (selectedRate !== null) return selectedRate;

  const lineTotal = getFirstFiniteNumber(scope, ["totalExclGst"]);
  if (lineTotal !== null) {
    if (Array.isArray(scope?.materials) && scope.materials.length > 0) {
      return lineTotal;
    }

    const quantity = getFirstFiniteNumber(scope, ["quantity"]);
    return quantity > 0 ? lineTotal / quantity : lineTotal;
  }

  const baseRate = getFirstFiniteNumber(scope, [
    "scopeRate",
    "basePrice",
    "currentRate",
    "rate",
    "itemRate",
  ]);
  const installationRate = getFirstFiniteNumber(scope, ["installationRate"]);

  if (baseRate === null && installationRate === null) return null;
  return (baseRate || 0) + (installationRate || 0);
};

const getQuotationRateItems = (quotationSet = {}, rfqType = "") => {
  if (rfqType === "material") {
    return (quotationSet?.materialDetails || []).map((item, index) => ({
      key: String(item?.id || item?.code || `${item?.name || "material"}-${index}`),
      name: item?.name || item?.code || `Material ${index + 1}`,
      quantity: item?.quantity,
      unit: item?.unit,
      rate: getMaterialRate(item),
      isSelected: item?.isSelected === true,
    }));
  }

  if (rfqType === "labour") {
    return (quotationSet?.labourItems || []).map((item, index) => ({
      key: String(item?.id || `${item?.skill || "labour"}-${index}`),
      name: item?.skill || `Labour item ${index + 1}`,
      quantity: item?.quantity,
      unit: item?.unit,
      rate: getLabourRate(item),
      isSelected: item?.isSelected === true,
    }));
  }

  if (rfqType === "contractor") {
    return (quotationSet?.contractorDetails || []).flatMap((section, sectionIndex) =>
      (section?.scopes || []).map((scope, scopeIndex) => ({
        key: String(
          scope?.id ||
            `${section?.id || sectionIndex}-${scope?.scopeOfWork || scopeIndex}`,
        ),
        name:
          scope?.scopeOfWork ||
          section?.sectionName ||
          `Scope ${scopeIndex + 1}`,
        sectionName: sanitizeHtmlText(section?.sectionName),
        quantity: scope?.quantity,
        unit: scope?.unit,
        rate: getContractorRate(scope),
        isSelected: scope?.isSelected === true,
      })),
    );
  }

  return [];
};

const getQuotationRateChanges = (
  previousQuotationSet,
  currentQuotationSet,
  rfqType,
  rfqData,
) => {
  if (!previousQuotationSet || !currentQuotationSet) return [];

  const rfqLineItems = rfqType === "material"
    ? rfqData?.materials || []
    : rfqType === "labour"
      ? rfqData?.labourDetails || []
      : (rfqData?.providingFixingDetails || []).flatMap(
          (section) => section?.scopes || [],
        );
  const rfqItemsById = new Map(
    rfqLineItems
      .filter((item) => item?._id || item?.id)
      .map((item) => [String(item?._id || item?.id), item]),
  );

  const previousItems = new Map(
    getQuotationRateItems(previousQuotationSet, rfqType).map((item) => [
      item.key,
      item,
    ]),
  );

  return getQuotationRateItems(currentQuotationSet, rfqType).reduce(
    (changes, currentItem) => {
      const previousItem = previousItems.get(currentItem.key);
      if (!previousItem || currentItem.rate === null) return changes;

      const hasRateChanged =
        previousItem.rate === null ||
        Math.abs(previousItem.rate - currentItem.rate) >= 0.005;
      const isSelectedContractorScope =
        rfqType === "contractor" && currentItem.isSelected;
      if (!hasRateChanged && !isSelectedContractorScope) return changes;

      changes.push({
        key: currentItem.key,
        name: currentItem.name,
        sectionName: currentItem.sectionName || previousItem.sectionName || "",
        quantity:
          currentItem.quantity ?? previousItem.quantity ??
          rfqItemsById.get(currentItem.key)?.quantity,
        unit:
          currentItem.unit || previousItem.unit ||
          rfqItemsById.get(currentItem.key)?.unit || "",
        previousRate: previousItem.rate,
        updatedRate: currentItem.rate,
        difference:
          previousItem.rate === null
            ? null
            : currentItem.rate - previousItem.rate,
      });
      return changes;
    },
    [],
  );
};

const getQuotationTextChangeItems = (quotationSet = {}, rfqType) => {
  if (rfqType === "material") {
    return (quotationSet?.materialItems || []).map((item, index) => ({
      key: String(item?.id || item?.code || `${item?.name || "material"}-${index}`),
      name: item?.name || item?.code || `Material ${index + 1}`,
      fields: [
        {
          field: "remarks",
          label: "Remarks",
          value: item?.remarks ?? item?.remark,
        },
      ],
    }));
  }

  if (rfqType === "labour") {
    return (quotationSet?.labourItems || []).map((item, index) => ({
      key: String(item?.id || `${item?.skill || "labour"}-${index}`),
      name: item?.skill || `Labour item ${index + 1}`,
      fields: [
        {
          field: "remarks",
          label: "Remarks",
          value: item?.remarks ?? item?.remark,
        },
      ],
    }));
  }

  if (rfqType === "contractor") {
    return (quotationSet?.contractorDetails || []).flatMap((section, sectionIndex) =>
      (section?.scopes || []).flatMap((scope, scopeIndex) => {
        const scopeKey = String(
          scope?.id ||
            `${section?.id || sectionIndex}-${scope?.scopeOfWork || scopeIndex}`,
        );
        const scopeName =
          scope?.scopeOfWork ||
          section?.sectionName ||
          `Scope ${scopeIndex + 1}`;

        const scopeItem = {
          key: scopeKey,
          name: scopeName,
          sectionName: sanitizeHtmlText(section?.sectionName),
          fields: [
            {
              field: "comments",
              label: "Comments / Methodology",
              value: scope?.comments ?? scope?.remarks ?? scope?.remark,
            },
          ],
        };

        const materialItems = (scope?.materials || []).map((material, materialIndex) => ({
          key: String(
            material?.id ||
              material?._id ||
              `${scopeKey}-material-${material?.name || materialIndex}`,
          ),
          name: material?.name
            ? `${scopeName} - ${material.name}`
            : `${scopeName} - Material ${materialIndex + 1}`,
          sectionName: sanitizeHtmlText(section?.sectionName),
          fields: [
            {
              field: "remarks",
              label: "Comments / Methodology",
              value: material?.remarks ?? material?.remark ?? material?.comments,
            },
          ],
        }));

        return [scopeItem, ...materialItems];
      }),
    );
  }

  return [];
};

const getQuotationTextChanges = (
  previousQuotationSet,
  currentQuotationSet,
  rfqType,
) => {
  if (!previousQuotationSet || !currentQuotationSet) return [];

  const previousItems = new Map(
    getQuotationTextChangeItems(previousQuotationSet, rfqType).map((item) => [
      item.key,
      item,
    ]),
  );

  return getQuotationTextChangeItems(currentQuotationSet, rfqType).reduce(
    (changes, currentItem) => {
      const previousItem = previousItems.get(currentItem.key);
      if (!previousItem) return changes;

      currentItem.fields.forEach((currentField) => {
        const previousField = (previousItem.fields || []).find(
          (field) => field.field === currentField.field,
        );
        const previousValue = sanitizeHtmlText(previousField?.value);
        const updatedValue = sanitizeHtmlText(currentField?.value);

        if (previousValue === updatedValue) return;
        if (!previousValue && !updatedValue) return;

        changes.push({
          key: `${currentItem.key}-${currentField.field}`,
          field: currentField.field,
          label: `${sanitizeHtmlText(currentItem.name)} - ${currentField.label}`,
          sectionName: currentItem.sectionName || previousItem.sectionName || "",
          previousValue,
          updatedValue,
        });
      });

      return changes;
    },
    [],
  );
};

const getRateItemLabel = (rfqType) => {
  if (rfqType === "labour") return "Labour";
  if (rfqType === "contractor") return "Scope";
  return "Material";
};

const getQuotationSetAmount = (quotationSet = {}) => {
  const hasSelectedAmount = [
    quotationSet?.selectedTotalWithoutGst,
    quotationSet?.selectedOthersTotalWithoutGst,
  ].some((value) => value !== undefined && value !== null && value !== "");

  if (hasSelectedAmount) {
    return (
      Number(quotationSet?.selectedTotalWithoutGst || 0) +
      Number(quotationSet?.selectedOthersTotalWithoutGst || 0)
    );
  }

  return (
    Number(quotationSet?.totalWithoutGst || 0) +
    Number(quotationSet?.othersTotalWithoutGst || 0)
  );
};

const getQuotationSetIndexForRound = (quotationSets = [], round = {}) => {
  const roundNumber = Number(round?.roundNumber);
  if (
    Number.isInteger(roundNumber) &&
    roundNumber >= 0 &&
    quotationSets[roundNumber]
  ) {
    return roundNumber;
  }

  if (!isValidDate(round?.submittedAt)) return -1;
  const submittedTime = new Date(round.submittedAt).getTime();
  return quotationSets.findIndex(
    (quotationSet) =>
      isValidDate(quotationSet?.submittedAt) &&
      new Date(quotationSet.submittedAt).getTime() === submittedTime,
  );
};

const formatStatus = (status = "") => {
  const normalizedStatus = String(status || "").trim().toUpperCase();
  return (
    STATUS_LABELS[normalizedStatus] ||
    normalizedStatus
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
};

const formatCount = (count, singular, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const uniqueValues = (values = []) =>
  [...new Set(values.filter(Boolean).map((value) => String(value).trim()))];

const formatNames = (values = [], maxVisible = 3) => {
  const cleanedValues = uniqueValues(values);
  if (!cleanedValues.length) return "--";
  if (cleanedValues.length <= maxVisible) return cleanedValues.join(", ");
  return `${cleanedValues.slice(0, maxVisible).join(", ")} +${cleanedValues.length - maxVisible} more`;
};

const getCategoryLabel = (rfqData) =>
  rfqData?.category?.subCategoryName ||
  rfqData?.category?.categoryName ||
  rfqData?.name ||
  "--";

const getNegotiatedVendorsCount = (rfqData) =>
  (rfqData?.negotiationDetails?.vendorWiseNegotiationTrail || []).filter(
    (item) => Number(item?.changedRoundCount || 0) > 0,
  ).length;

const getAwardedSuppliers = (rfqData) =>
  (rfqData?.suppliers || []).filter(
    (supplier) =>
      Array.isArray(supplier?.closingDetails) && supplier.closingDetails.length > 0,
  );

const getVendorSourceSummary = (suppliers = []) => {
  const sourceCountMap = suppliers.reduce((summary, supplier) => {
    const source = String(supplier?.vendorSource || "UNKNOWN").toUpperCase();
    summary[source] = (summary[source] || 0) + 1;
    return summary;
  }, {});

  return Object.entries(sourceCountMap).map(([source, count]) => {
    const label =
      source === "MARKETPLACE"
        ? "Marketplace"
        : source === "INTERNAL"
          ? "Internal"
          : formatStatus(source);
    return `${label}: ${count}`;
  });
};

const getTimelineInvitedSuppliers = (rfqData) => {
  const allInvitedSuppliers = [
    ...(Array.isArray(rfqData?.suppliers) ? rfqData.suppliers : []),
    ...(Array.isArray(rfqData?.deletedSuppliers) ? rfqData.deletedSuppliers : []),
  ];
  const seenSuppliers = new Set();

  return allInvitedSuppliers.filter((supplier, index) => {
    const supplierKey = String(
      supplier?.supplierId ||
        supplier?._id ||
        `${supplier?.businessName || "vendor"}-${supplier?.addedAt || index}`,
    );
    if (seenSuppliers.has(supplierKey)) return false;

    seenSuppliers.add(supplierKey);
    return true;
  });
};

const pushTimelineEvent = (events, sequenceRef, event) => {
  if (!isValidDate(event?.timestamp)) return;

  events.push({
    id: event.id || `rfq-timeline-event-${sequenceRef.current}`,
    sequence: sequenceRef.current,
    tone: event.tone || "slate",
    title: event.title || "Timeline event",
    subtitle: event.subtitle || "",
    timestamp: event.timestamp,
    meta: Array.isArray(event.meta) ? event.meta.filter(Boolean) : [],
    rateChanges: Array.isArray(event.rateChanges) ? event.rateChanges : [],
    vendorNames: Array.isArray(event.vendorNames)
      ? uniqueValues(event.vendorNames)
      : [],
    fieldChanges: Array.isArray(event.fieldChanges) ? event.fieldChanges : [],
    itemChanges: Array.isArray(event.itemChanges) ? event.itemChanges : [],
    grnDetails: Array.isArray(event.grnDetails) ? event.grnDetails : [],
    rateItemLabel: event.rateItemLabel || "Item",
    tag: event.tag || "",
  });

  sequenceRef.current += 1;
};

const buildRfqEditEvents = (rfqData, events, sequenceRef) => {
  (rfqData?.editHistory || []).forEach((historyEntry, index) => {
    const fieldChanges = Array.isArray(historyEntry?.fieldChanges)
      ? historyEntry.fieldChanges
      : [];
    const itemChanges = Array.isArray(historyEntry?.itemChanges)
      ? historyEntry.itemChanges.map((itemChange) => {
          if (String(itemChange?.itemType || "").toLowerCase() !== "scope") {
            return itemChange;
          }

          const sectionIndex =
            Number.parseInt(String(itemChange?.rowIndex || "").split(".")[0], 10) - 1;
          const fallbackSectionName = Number.isInteger(sectionIndex)
            ? rfqData?.providingFixingDetails?.[sectionIndex]?.sectionName
            : "";

          return {
            ...itemChange,
            sectionName: sanitizeHtmlText(
              itemChange?.sectionName || fallbackSectionName,
            ),
          };
        })
      : [];
    if (!fieldChanges.length && !itemChanges.length) return;

    const changeSummary = [
      fieldChanges.length ? formatCount(fieldChanges.length, "field") : "",
      itemChanges.length ? formatCount(itemChanges.length, "line item") : "",
    ]
      .filter(Boolean)
      .join(" and ");

    pushTimelineEvent(events, sequenceRef, {
      id: `rfq-updated-${index}`,
      tone: "slate",
      tag: "RFQ Updated",
      title: `${historyEntry?.editedByName || "RFQ user"} updated RFQ details`,
      subtitle: `${changeSummary} changed`,
      timestamp: historyEntry?.editedAt,
      fieldChanges,
      itemChanges,
      meta: historyEntry?.editedByEmail
        ? [`Updated by: ${historyEntry.editedByName}`]
        : [],
    });
  });
};

const buildInviteEvents = (rfqData, events, sequenceRef) => {
  const suppliers = getTimelineInvitedSuppliers(rfqData);
  if (!suppliers.length) return;

  const groupedInvites = suppliers.reduce((groups, supplier, index) => {
    const eventTime = resolveTimelineTimestamp(
      supplier?.addedAt,
      rfqData?.createdAt,
      rfqData?.createdAt,
    );
    if (!isValidDate(eventTime)) return groups;

    const groupKey = `${eventTime}-${supplier?.addedBy?.userId || "unknown"}-${index < suppliers.length ? "invite" : ""}`;
    if (!groups[groupKey]) {
      groups[groupKey] = {
        timestamp: eventTime,
        suppliers: [],
        addedByName: supplier?.addedBy?.name || rfqData?.requesterName || "",
      };
    }

    groups[groupKey].suppliers.push(supplier);
    return groups;
  }, {});

  Object.values(groupedInvites).forEach((group, index) => {
    const invitedSuppliers = group?.suppliers || [];
    pushTimelineEvent(events, sequenceRef, {
      id: `vendors-invited-${index}`,
      tone: "blue",
      tag: "Vendors Invited",
      title: `${formatCount(invitedSuppliers.length, "vendor")} invited`,
      subtitle: `Invited by ${group.addedByName || "RFQ creator"}`,
      vendorNames: invitedSuppliers.map(
        (supplier) => supplier?.businessName || "Unnamed vendor",
      ),
      timestamp: group.timestamp,
      meta: getVendorSourceSummary(invitedSuppliers),
    });
  });
};

const buildExcludedVendorEvents = (rfqData, events, sequenceRef) => {
  const excludedSuppliers = getTimelineInvitedSuppliers(rfqData).filter(
    (supplier) => supplier?.exclusionDetails?.isExcludedFromComparison === true,
  );

  excludedSuppliers.forEach((supplier, index) => {
    const exclusionDetails = supplier?.exclusionDetails || {};
    const excludedTimestamp = resolveTimelineTimestamp(
      exclusionDetails.excludedAt,
      supplier?.updatedAt || rfqData?.updatedAt,
      rfqData?.createdAt,
    );
    if (!excludedTimestamp) return;

    const vendorName = supplier?.businessName || "Unnamed vendor";
    const excludedBy =
      typeof exclusionDetails.excludedBy === "string"
        ? exclusionDetails.excludedBy
        : exclusionDetails.excludedBy?.name;
    const reason = sanitizeHtmlText(exclusionDetails.reason);
    const rankWhenExcluded =
      exclusionDetails.rankWhenExcluded || supplier?.vendorQuotations?.rank;
    const quotationWasRetained =
      supplier?.quotationReceived === true ||
      (supplier?.vendorQuotations?.quotationSets || []).length > 0;
    const vendorKey = String(
      supplier?.supplierId || supplier?._id || `${vendorName}-${index}`,
    );

    pushTimelineEvent(events, sequenceRef, {
      id: `vendor-excluded-${vendorKey}-${exclusionDetails.excludedAt || ""}`,
      tone: "yellow",
      tag: "Vendor Excluded",
      title: `${vendorName} excluded from comparison`,
      subtitle: "Vendor removed from quotation comparison",
      vendorNames: [vendorName],
      timestamp: excludedTimestamp,
      meta: [
        excludedBy ? `Excluded by: ${excludedBy}` : "",
        reason ? `Reason: ${reason}` : "",
        rankWhenExcluded ? `Rank when excluded: ${rankWhenExcluded}` : "",
        supplier?.vendorSource
          ? `Source: ${formatStatus(supplier.vendorSource)}`
          : "",
        quotationWasRetained ? "Quotation retained" : "",
      ],
    });
  });
};

const buildDeletedVendorEvents = (rfqData, events, sequenceRef) => {
  const deletedSuppliers = [
    ...(Array.isArray(rfqData?.deletedSuppliers) ? rfqData.deletedSuppliers : []),
    ...(Array.isArray(rfqData?.suppliers)
      ? rfqData.suppliers.filter(
          (supplier) =>
            supplier?.isDeleted === true ||
            String(supplier?.state || "").toLowerCase() === "deleted",
        )
      : []),
  ];
  const processedDeletedVendors = new Set();

  deletedSuppliers.forEach((supplier, index) => {
    const vendorKey = `${String(
      supplier?.supplierId || supplier?._id || supplier?.businessName || index,
    )}-${supplier?.deletedAt || ""}`;
    if (processedDeletedVendors.has(vendorKey)) return;
    processedDeletedVendors.add(vendorKey);

    const deletedTimestamp = resolveTimelineTimestamp(
      supplier?.deletedAt,
      supplier?.updatedAt || rfqData?.updatedAt,
      rfqData?.createdAt,
    );
    if (!deletedTimestamp) return;

    const vendorName = supplier?.businessName || "Unnamed vendor";
    const quotationWasRetained =
      supplier?.quotationReceived === true ||
      (supplier?.vendorQuotations?.quotationSets || []).length > 0;

    pushTimelineEvent(events, sequenceRef, {
      id: `vendor-deleted-${vendorKey}`,
      tone: "red",
      tag: "Vendor Deleted",
      title: `${vendorName} deleted from RFQ`,
      subtitle: "Vendor moved to Deleted Vendors",
      vendorNames: [vendorName],
      timestamp: deletedTimestamp,
      meta: [
        supplier?.deletedBy?.name
          ? `Deleted by: ${supplier.deletedBy.name}`
          : "",
        supplier?.addedAt ? `Invited on: ${formatDateTime(supplier.addedAt)}` : "",
        supplier?.vendorSource
          ? `Source: ${formatStatus(supplier.vendorSource)}`
          : "",
        quotationWasRetained ? "Quotation retained" : "",
      ],
    });
  });
};

const buildPurchaseOrderEvents = (rfqData, events, sequenceRef) => {
  const suppliers = getTimelineInvitedSuppliers(rfqData);

  suppliers.forEach((supplier, supplierIndex) => {
    const poDetails = Array.isArray(supplier?.poDetails)
      ? supplier.poDetails
      : [];

    poDetails.forEach((purchaseOrder, poIndex) => {
      const poNumber = purchaseOrder?.poNumber || `PO ${poIndex + 1}`;
      const createdOn = purchaseOrder?.createdOn || purchaseOrder?.createdAt;
      if (!isValidDate(createdOn)) return;

      const grnDetails = Array.isArray(purchaseOrder?.grnDetails)
        ? purchaseOrder.grnDetails.filter(
            (grn) =>
              grn &&
              (grn.grnNumber || grn.grnDate || sanitizeHtmlText(grn.grnComments)),
          )
        : [];
      pushTimelineEvent(events, sequenceRef, {
        id: `purchase-order-created-${supplier?.supplierId || supplierIndex}-${poNumber}`,
        tone: "yellow",
        tag: "PO Created",
        title: `Purchase order ${poNumber} created`,
        subtitle: `${supplier?.businessName || "Vendor"} · ${formatAmount(
          purchaseOrder?.poAmount,
        )}`,
        timestamp: createdOn,
        grnDetails: grnDetails.map((grn) => ({
          grnNumber: grn?.grnNumber || "--",
          grnDate: grn?.grnDate || null,
          grnComments: grn?.grnComments || "",
        })),
        meta: [
          purchaseOrder?.createdByName
            ? `Created by: ${purchaseOrder.createdByName}`
            : "",
          purchaseOrder?.remarks
            ? `Remarks: ${sanitizeHtmlText(purchaseOrder.remarks)}`
            : "",
        ],
      });
    });
  });
};

const buildQuotationEvents = (rfqData, events, sequenceRef) => {
  (rfqData?.suppliers || []).forEach((supplier, index) => {
    if (!supplier?.quotationReceived) return;

    const quotationSets = supplier?.vendorQuotations?.quotationSets || [];
    const firstQuotation =
      quotationSets.find((item) => isValidDate(item?.submittedAt)) || null;

    const quotationTimestamp = resolveTimelineTimestamp(
      firstQuotation?.submittedAt,
      rfqData?.updatedAt,
      rfqData?.createdAt,
    );
    if (!quotationTimestamp) return;

    const quotationValue =
      Number(firstQuotation?.grandTotalWithoutGst) ||
      Number(firstQuotation?.totalWithoutGst || 0) +
        Number(firstQuotation?.othersTotalWithoutGst || 0);
    const quotationRank =
      supplier?.vendorQuotations?.rank ||
      supplier?.vendorQuotations?.rankWithGst ||
      supplier?.quotationDetails?.rank;

    pushTimelineEvent(events, sequenceRef, {
      id: `quotation-received-${supplier?.supplierId || index}`,
      tone: "green",
      tag: "Quotation Received",
      title: `Quotation received from ${supplier?.businessName || "vendor"}`,
      subtitle: `Submitted by ${firstQuotation?.submittedBy || "vendor"}${
        quotationValue > 0 ? ` for ${formatAmount(quotationValue)}` : ""
      }`,
      timestamp: quotationTimestamp,
      meta: [
        supplier?.vendorSource
          ? `Source: ${formatStatus(supplier.vendorSource)}`
          : "",
        "First quotation submitted",
        quotationRank ? `Rank: ${quotationRank}` : "",
      ],
    });
  });
};

const buildNegotiationEvents = (rfqData, events, sequenceRef) => {
  const vendorWiseTrail =
    rfqData?.negotiationDetails?.vendorWiseNegotiationTrail || [];
  const handledQuotationUpdates = new Set();

  vendorWiseTrail.forEach((trail, vendorIndex) => {
    const supplier = (rfqData?.suppliers || []).find(
      (item) => String(item?.supplierId) === String(trail?.supplierId),
    );
    const quotationSets = supplier?.vendorQuotations?.quotationSets || [];

    (trail?.rounds || [])
      .filter(
        (round) =>
          Number(round?.roundNumber || 0) > 0 ||
          !String(round?.label || "").toLowerCase().includes("original"),
      )
      .forEach((round, roundIndex) => {
        const quotationSetIndex = getQuotationSetIndexForRound(
          quotationSets,
          round,
        );
        const currentQuotationSet = quotationSetIndex > 0
          ? quotationSets[quotationSetIndex]
          : null;
        const previousQuotationSet = quotationSetIndex > 0
          ? quotationSets[quotationSetIndex - 1]
          : null;
        const rateChanges = getQuotationRateChanges(
          previousQuotationSet,
          currentQuotationSet,
          rfqData?.rfqType,
          rfqData,
        );
        const fieldChanges = getQuotationTextChanges(
          previousQuotationSet,
          currentQuotationSet,
          rfqData?.rfqType,
        );
        const currentAmount = Number(round?.amount || 0);
        const previousAmount = Number(round?.previousAmount || 0);
        const savings = previousAmount > 0
          ? previousAmount - currentAmount
          : Number(round?.savings || 0);
        const negotiationTimestamp = resolveTimelineTimestamp(
          round?.submittedAt,
          rfqData?.negotiationDetails?.lastCalculatedAt || rfqData?.updatedAt,
          rfqData?.createdAt,
        );

        if (!negotiationTimestamp) return;

        if (currentQuotationSet) {
          handledQuotationUpdates.add(
            `${String(trail?.supplierId)}-${quotationSetIndex}`,
          );
        }

        pushTimelineEvent(events, sequenceRef, {
          id: `negotiation-${vendorIndex}-${roundIndex}`,
          tone: "purple",
          tag: "Quotation Updated",
          title: `${trail?.supplierName || "Vendor"} updated quotation`,
          subtitle: `${round?.label || `Round ${round?.roundNumber}`}: ${
            previousAmount > 0
              ? `${formatAmount(previousAmount)} to ${formatAmount(currentAmount)}`
              : formatAmount(currentAmount)
          }`,
          timestamp: negotiationTimestamp,
          fieldChanges,
          rateChanges,
          rateItemLabel: getRateItemLabel(rfqData?.rfqType),
          meta: [
            round?.submittedBy ? `Updated by ${round.submittedBy}` : "",
            savings > 0
              ? `Savings: ${formatAmount(savings)}`
              : savings < 0
                ? `Increase: ${formatAmount(Math.abs(savings))}`
                : "No savings captured",
            trail?.rank ? `Rank: ${trail.rank}` : "",
          ],
        });
      });
  });

  (rfqData?.suppliers || []).forEach((supplier, vendorIndex) => {
    const quotationSets = supplier?.vendorQuotations?.quotationSets || [];

    quotationSets.slice(1).forEach((currentQuotationSet, updateIndex) => {
      const quotationSetIndex = updateIndex + 1;
      const updateKey = `${String(supplier?.supplierId)}-${quotationSetIndex}`;
      if (handledQuotationUpdates.has(updateKey)) return;

      const previousQuotationSet = quotationSets[quotationSetIndex - 1];
      const negotiationTimestamp = resolveTimelineTimestamp(
        currentQuotationSet?.submittedAt,
        rfqData?.updatedAt,
        rfqData?.createdAt,
      );
      if (!negotiationTimestamp) return;

      const previousAmount = getQuotationSetAmount(previousQuotationSet);
      const currentAmount = getQuotationSetAmount(currentQuotationSet);
      const savings = previousAmount - currentAmount;
      const rateChanges = getQuotationRateChanges(
        previousQuotationSet,
        currentQuotationSet,
        rfqData?.rfqType,
        rfqData,
      );
      const fieldChanges = getQuotationTextChanges(
        previousQuotationSet,
        currentQuotationSet,
        rfqData?.rfqType,
      );

      pushTimelineEvent(events, sequenceRef, {
        id: `quotation-update-${supplier?.supplierId || vendorIndex}-${quotationSetIndex}`,
        tone: "purple",
        tag: "Quotation Updated",
        title: `${supplier?.businessName || "Vendor"} updated quotation`,
        subtitle: `Round ${quotationSetIndex}: ${
          previousAmount > 0
            ? `${formatAmount(previousAmount)} to ${formatAmount(currentAmount)}`
            : formatAmount(currentAmount)
        }`,
        timestamp: negotiationTimestamp,
        fieldChanges,
        rateChanges,
        rateItemLabel: getRateItemLabel(rfqData?.rfqType),
        meta: [
          currentQuotationSet?.submittedBy
            ? `Updated by ${currentQuotationSet.submittedBy}`
            : "",
          savings > 0
            ? `Savings: ${formatAmount(savings)}`
            : savings < 0
              ? `Increase: ${formatAmount(Math.abs(savings))}`
              : "No savings captured",
          supplier?.vendorQuotations?.rank
            ? `Rank: ${supplier.vendorQuotations.rank}`
            : "",
        ],
      });
    });
  });
  };

const buildApprovalEvents = (rfqData, events, sequenceRef) => {
  const approvalDetails = rfqData?.approvalDetails || [];
  const approvalComments = rfqData?.commentsToApprover || [];

  approvalDetails.forEach((level, index) => {
    if (level?.requestedAt) {
      const matchingComment = approvalComments[index];

      pushTimelineEvent(events, sequenceRef, {
        id: `approval-request-${level?.level || index}`,
        tone: "orange",
        tag: "Sent For Approval",
        title: `Sent for approval - Level ${level?.level || index + 1}`,
        subtitle: `Approver${level?.approvers?.length > 1 ? "s" : ""}: ${formatNames(
          (level?.approvers || []).map((approver) => approver?.name || approver?.email),
        )}`,
        timestamp: level?.requestedAt,
        meta: [
          level?.threshold ? `Threshold: ${formatAmount(level.threshold)}` : "",
          matchingComment?.comment
            ? `Comment: ${sanitizeHtmlText(matchingComment.comment)}`
            : "",
        ],
      });
    }

    const approvalActionTimestamp = level?.approvedAt || level?.rejectedAt;
    if (approvalActionTimestamp && level?.status) {
      const normalizedStatus = String(level.status).toUpperCase();

      pushTimelineEvent(events, sequenceRef, {
        id: `approval-action-${level?.level || index}`,
        tone: normalizedStatus === "APPROVED" ? "green" : "red",
        tag: "Approval Decision",
        title: `Level ${level?.level || index + 1} ${formatStatus(level.status)}`,
        subtitle:
          normalizedStatus === "APPROVED"
            ? `Approved by ${level?.approvedBy?.name || "approver"}`
            : `Rejected by ${level?.approvedBy?.name || "approver"}`,
        timestamp: approvalActionTimestamp,
        meta: [
          level?.approvedAmount
            ? `Approved amount: ${formatAmount(level.approvedAmount)}`
            : "",
          level?.rejectionRemark
            ? `Remark: ${sanitizeHtmlText(level.rejectionRemark)}`
            : "",
        ],
      });
    }
  });
};

const buildStatusEvents = (rfqData, events, sequenceRef) => {
  (rfqData?.statusLogs || [])
    .filter((log) => isValidDate(log?.date))
    .forEach((log, index) => {
      const statusFrom = String(log?.changedStatusFrom || "").toUpperCase();
      const statusTo = String(log?.changedStatusTo || "").toUpperCase();
      if (statusTo === "PENDING APPROVAL" || statusTo === "CLOSED") return;

      const isReopenedAfterPendingApproval =
        statusFrom === "PENDING APPROVAL" && statusTo === "OPEN";

      pushTimelineEvent(events, sequenceRef, {
        id: `status-log-${index}`,
        tone: "slate",
        tag: "Status Update",
        title: `Status changed to ${formatStatus(statusTo)}`,
        subtitle: statusFrom
          ? `Moved from ${formatStatus(statusFrom)}`
          : "Status updated",
        timestamp: log?.date,
        meta: [
          isReopenedAfterPendingApproval && log?.name
            ? `Changed by: ${log.name}`
            : "",
        ],
      });
    });
};

const buildClosingEvent = (rfqData, events, sequenceRef) => {
  const closingDate =
    rfqData?.rfqClosingDetails?.date ||
    (rfqData?.statusLogs || []).find(
      (item) => String(item?.changedStatusTo || "").toUpperCase() === "CLOSED",
    )?.date;

  if (!closingDate) return;

  const awardedSuppliers = getAwardedSuppliers(rfqData);
  const totalAwardedValue = awardedSuppliers.reduce((sum, supplier) => {
    const closingAmount = Number(supplier?.closingDetails?.[0]?.amount || 0);
    return sum + (Number.isFinite(closingAmount) ? closingAmount : 0);
  }, 0);

  const awardJustifications = (rfqData?.awardJustifications || [])
    .flatMap((item) => item?.options || [])
    .filter(Boolean);

  pushTimelineEvent(events, sequenceRef, {
    id: "rfq-closing",
    tone: "red",
    tag: "Closing Details",
    title:
      String(rfqData?.status || "").toUpperCase() === "CLOSED"
        ? "RFQ closed"
        : "RFQ closing updated",
    subtitle: awardedSuppliers.length
      ? `Awarded to ${formatNames(
          awardedSuppliers.map((supplier) => supplier?.businessName),
        )}`
      : "No awarded vendor details captured",
    timestamp: closingDate,
    meta: [
      totalAwardedValue > 0 ? `Awarded amount: ${formatAmount(totalAwardedValue)}` : "",
      rfqData?.budgetDifference !== undefined && rfqData?.budgetDifference !== null
        ? `Budget difference: ${formatAmount(rfqData.budgetDifference)}`
        : "",
      awardJustifications.length
        ? `Award justifications: ${formatNames(awardJustifications, 4)}`
        : "",
    ],
  });
};

export const buildRfqTimelineEvents = (rfqData) => {
  if (!rfqData) return [];

  const events = [];
  const sequenceRef = { current: 0 };

  pushTimelineEvent(events, sequenceRef, {
    id: "rfq-created",
    tone: "orange",
    tag: "RFQ Created",
    title: "RFQ created",
    subtitle: `${rfqData?.requesterName || "Requester"} created ${
      rfqData?.name || "this RFQ"
    } for ${rfqData?.projectName || "the selected project"}`,
    timestamp: rfqData?.createdAt,
    meta: [
      `RFQ ID: ${rfqData?.rfqId || "--"}`,
      `Type: ${RFQ_TYPE_LABELS[rfqData?.rfqType] || formatStatus(rfqData?.rfqType)}`,
      `Category: ${getCategoryLabel(rfqData)}`,
      rfqData?.rfqBudget ? `Budget: ${formatAmount(rfqData.rfqBudget)}` : "",
    ],
  });

  buildRfqEditEvents(rfqData, events, sequenceRef);
  buildInviteEvents(rfqData, events, sequenceRef);
  buildExcludedVendorEvents(rfqData, events, sequenceRef);
  buildDeletedVendorEvents(rfqData, events, sequenceRef);
  buildPurchaseOrderEvents(rfqData, events, sequenceRef);
  buildQuotationEvents(rfqData, events, sequenceRef);
  buildNegotiationEvents(rfqData, events, sequenceRef);
  buildApprovalEvents(rfqData, events, sequenceRef);
  buildStatusEvents(rfqData, events, sequenceRef);
  buildClosingEvent(rfqData, events, sequenceRef);

  return events.sort((left, right) => {
    const leftTime = new Date(left.timestamp).getTime();
    const rightTime = new Date(right.timestamp).getTime();

    if (leftTime === rightTime) {
      return left.sequence - right.sequence;
    }

    return leftTime - rightTime;
  });
};

export const buildRfqTimelineSummary = (rfqData) => {
  const invitedSuppliers = getTimelineInvitedSuppliers(rfqData);
  const quotationSuppliers = invitedSuppliers.filter(
    (supplier) => supplier?.quotationReceived,
  );
  const approvalLevels = rfqData?.approvalDetails || [];
  const awardedSuppliers = getAwardedSuppliers(rfqData);
  const normalizedStatus = String(rfqData?.status || "").toUpperCase();
  const isProposedClosure =
    normalizedStatus === "PENDING APPROVAL" || normalizedStatus === "REWORK";

  return [
    {
      key: "invited",
      label: "Vendors Invited",
      value: invitedSuppliers.length,
      helper: quotationSuppliers.length
        ? `${quotationSuppliers.length} responded`
        : "No quotations yet",
    },
    {
      key: "quotations",
      label: "Quotations Received",
      value: quotationSuppliers.length,
      helper:
        invitedSuppliers.length > 0
          ? `${invitedSuppliers.length - quotationSuppliers.length} pending`
          : "Awaiting vendor invite",
    },
    {
      key: "negotiations",
      label: "Negotiated Vendors",
      value: getNegotiatedVendorsCount(rfqData),
      helper: rfqData?.negotiationDetails?.lastCalculatedAt
        ? `Updated ${formatDateTime(rfqData.negotiationDetails.lastCalculatedAt)}`
        : "No negotiation trail",
    },
    {
      key: "approvals",
      label: "Approval Stages",
      value: approvalLevels.length,
      helper: approvalLevels.length
        ? `${approvalLevels.filter((level) => level?.status === "APPROVED").length} approved`
        : "No approval flow",
    },
    {
      key: "awarded",
      label: isProposedClosure ? "Proposed Vendors" : "Awarded Vendors",
      value: awardedSuppliers.length,
      helper: awardedSuppliers.length
        ? formatAmount(
            awardedSuppliers.reduce(
              (sum, supplier) =>
                sum + Number(supplier?.closingDetails?.[0]?.amount || 0),
              0,
            ),
          )
        : isProposedClosure
          ? "No vendors proposed"
          : "Not awarded",
    },
  ];
};

export const getTimelineToneClasses = (tone) =>
  TONE_CLASS_MAP[tone] || TONE_CLASS_MAP.slate;

export const getRfqTimelineHeroDetails = (rfqData) => [
  {
    label: "Status",
    value: formatStatus(rfqData?.status),
  },
  {
    label: "Project",
    value: rfqData?.projectName || "--",
  },
  {
    label: "RFQ Type",
    value: RFQ_TYPE_LABELS[rfqData?.rfqType] || formatStatus(rfqData?.rfqType),
  },
  {
    label: "Category",
    value: getCategoryLabel(rfqData),
  },
];

export {
  formatAmount,
  formatDateTime,
  formatDateTimeParts,
  formatTimelineChangeValue,
  sanitizeHtmlText,
  formatStatus,
};
